/* @flow */
import { delay } from 'redux-saga'
import { takeLatest, fork, put, call } from 'redux-saga/effects'
import { error } from 'react-notification-system-redux'
import { I18n } from 'react-redux-i18n'
import { showAlert } from '../actions/global'
import moment from 'moment'
import _ from 'lodash'

import {
  PLACE_ORDER,
  GET_ORDERS,
  GET_ORDERS_SUCCESS,
  CANCEL_ORDER,
  GET_ACTIVITIES,
  SEND_MESSAGE,
  GET_ORDER_BY_ID,
  CONFIRM_ORDER,
  UPDATE_ORDER,
} from '../configs/constants'
import { setCurrentOrder } from '../actions/order'
import API from '../libs/api'

const api = new API()

export function* fetchOrders({ type, creator }) {
  try {
    const all = yield call(api.readDBOnce, '/orders')
    const orders = _.filter(all, ['creator', creator])
    yield (put({ type: GET_ORDERS_SUCCESS, orders }))
  } catch (err) {
    yield put(error({ title: 'Error: ' + err.code, message: err.message }))
  }
}

export function* fetchOrderById({ orderNo }) {
  try {
    const all = yield call(api.readDBOnce, '/orders')
    const order = _.find(all, ['id', orderNo])
    yield put(setCurrentOrder(order))
  } catch (err) {
    yield put(error({ title: 'Error: ' + err.code, message: err.message }))
  }
}

export function* updateTimeSectionTransaction(order, retry, isDelete = false) {
  try {
    yield call(api.transaction, 'activities/'+order.activityId, (activity) => {
      if (activity) {
        let targetSection = _.find(activity.availableList, { startTime: order.timeSection })
        let timestamp = moment(order.date, 'YYYY/MM/DD').valueOf()
        if (isDelete) {
          let total = targetSection[timestamp].booked - parseFloat(order.peopleCount)
          targetSection[timestamp].booked = total
        } else {
          if (!targetSection[timestamp]) {
            targetSection[timestamp] = { booked: 0 }
          }
          let booked = targetSection[timestamp].booked || 0
          let total = booked + parseFloat(order.peopleCount)
          if (total > targetSection.maxPeople) {
            return
          } else {
            targetSection[timestamp].booked = total
          }
        }
        return activity
      } else {
        if (retry) {
          throw { retry }
        }
      }
    }, (error, committed, snapshot) => {
      if (error) {
        throw { code: 'Unexpected error', message: error }
      } else if (!committed) {
        throw { code: isDelete ? I18n.t('cancelOrderFailed') : I18n.t('createOrderFailed'), message: isDelete ? I18n.t('cancelOrderFailed') : I18n.t('maxPeopleExceed') }
      }
    })
  } catch (err) {
    if (err.retry) {
      yield delay(500)
      yield call(updateTimeSectionTransaction, order, false, isDelete)
    } else {
      throw err
    }
  }
}

export function* placeOrder({ type, payload }) {
  try {
    const { order, status } = payload
    const {
      activityFlow,
      availableList,
      cancelRule,
      comments,
      content,
      intro,
      limitation,
      notice,
      selectedAvailable,
      favoriteKey,
      ...slimOrder
    } = order

    // Generate order id and No.
    const orderId = moment(order.startDate).format('YYYYMMDD') + '_' + (new Date().getTime())
    const newOrder = {
      ...slimOrder,
      id: orderId,
      orderNo: orderId,
      creator: payload.account.email,
      createdTime: moment().toString(),
      status,
    }
    yield updateTimeSectionTransaction(newOrder, true)
    yield call(api.writeDB, '/orders/' + newOrder.id, newOrder)

    // Refresh order list
    yield put({ type: GET_ORDERS, creator: newOrder.creator })
    // Refresh activity list
    yield put({ type: GET_ACTIVITIES })

    // Notify admin
    yield put({ type: SEND_MESSAGE, payload: {
      type: 'system',
      activityName: newOrder.name,
      shopId: newOrder.shopId,
      creator: payload.account.displayName,
      email: payload.account.email,
      title: '新訂單建立',
      message: '訂單編號: ' + newOrder.orderNo,
      status: 'unread',
    }})

    yield put( showAlert(
      I18n.t('orderSuccessResult', {
        orderNo: newOrder.orderNo,
        urOrders: '<a href="/account">' + I18n.t('yourOrder') + '</a>'
      }),
      I18n.t('orderSuccess'),
      'success',
      false,
      null,
      () => {
        window.location.href = '/orders/' + newOrder.orderNo
      }
    ) )
  } catch (err) {
    console.log('err: ', err)
    yield put(error({ title: 'Error: ' + err.code, message: err.message }))
  }
}

export function* updateOrder({ payload }) {
  try {
    yield call(api.updateDB, '/orders/' + payload.orderNo, { coupon: payload.coupon, discountId: payload.discountId, discount: payload.discount })
  } catch (err) {
    yield put(error({ title: 'Error on updating the order: ' + err.code, message: err.message }))
  }
}

export function* confirmOrder({ orderNo }) {
  try {
    yield call(api.updateDB, '/orders/' + orderNo, { status: 'unpaid' })
    yield put( showAlert(
      I18n.t('confirmOrderResult', { orderNo }),
      I18n.t('confirmOrderSuccess'),
      'success'
    ) )
  } catch (err) {
    yield put(error({ title: 'Error on cancelling the order: ' + err.code, message: err.message }))
  }
}

export function* cancelOrder({ type, order }) {
  try {
    yield call(api.updateDB, '/orders/' + order.id, { status: 'cancelled' })
    yield updateTimeSectionTransaction(order, true, true)
    yield put( showAlert(
      I18n.t('cancelOrderResult', {
        orderNo: order.id
      }),
      I18n.t('cancelOrderSuccess'),
      'success'
    ) )
    document.location.reload()
  } catch (err) {
    yield put(error({ title: 'Error on cancelling the order: ' + err.code, message: err.message }))
  }
}

/* Watchers */
export function* getOrdersWatcher() {
  yield takeLatest(GET_ORDERS, fetchOrders)
}
export function* getOrderByIdWatcher() {
  yield takeLatest(GET_ORDER_BY_ID, fetchOrderById)
}
export function* placeOrderWatcher() {
  yield takeLatest(PLACE_ORDER, placeOrder)
}
export function* updateOrderWatcher() {
  yield takeLatest(UPDATE_ORDER, updateOrder)
}
export function* confirmOrderWatcher() {
  yield takeLatest(CONFIRM_ORDER, confirmOrder)
}
export function* cancelOrderWatcher() {
  yield takeLatest(CANCEL_ORDER, cancelOrder)
}

export default [
  fork(getOrdersWatcher),
  fork(getOrderByIdWatcher),
  fork(placeOrderWatcher),
  fork(updateOrderWatcher),
  fork(confirmOrderWatcher),
  fork(cancelOrderWatcher),
]


// WEBPACK FOOTER //
// ./src/sagas/orderSaga.js